// apps/web/src/components/astral/AstralShareModal.tsx
import { useState } from "react";
import { Share2, Copy, Check, Globe, Lock, ExternalLink, X, Loader2 } from "lucide-react";

interface AstralShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  mapId: string;
  mapTitle: string;
  shareToken?: string | null;
  isPublic?: boolean;
  onTogglePublic?: (newIsPublic: boolean) => Promise<void>;
}

export function AstralShareModal({
  isOpen,
  onClose,
  mapId,
  mapTitle,
  shareToken,
  isPublic = true,
  onTogglePublic,
}: AstralShareModalProps) {
  const [copied, setCopied] = useState(false);
  const [toggling, setToggling] = useState(false);

  if (!isOpen) return null;

  const rawToken = (shareToken && shareToken.trim()) || (mapId && mapId.trim()) || "";
  const origin =
    typeof window !== "undefined" && window.location.origin
      ? window.location.origin
      : "https://falaunik.com";

  const fullShareUrl = rawToken ? `${origin}/mapa/${rawToken}` : "";

  const handleCopy = () => {
    if (!fullShareUrl) return;
    void navigator.clipboard.writeText(fullShareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2600);
    });
  };

  const handleToggle = async () => {
    if (!onTogglePublic) return;
    setToggling(true);
    try {
      await onTogglePublic(!isPublic);
    } finally {
      setToggling(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-panel"
        style={{
          width: "100%",
          maxWidth: "540px",
          backgroundColor: "#160a0a",
          border: "1px solid rgba(194, 160, 107, 0.3)",
          borderRadius: "20px",
          padding: "24px",
          boxShadow: "0 24px 60px rgba(0, 0, 0, 0.85), 0 0 30px rgba(194, 160, 107, 0.15)",
          color: "#ffffff",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        {/* Cabeçalho */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "42px",
                height: "42px",
                borderRadius: "12px",
                backgroundColor: "rgba(194, 160, 107, 0.15)",
                border: "1px solid rgba(194, 160, 107, 0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#c2a06b",
              }}
            >
              <Share2 size={20} />
            </div>
            <div>
              <h2 style={{ fontSize: "17px", fontWeight: 700, margin: 0, color: "#e3dac9" }}>
                Compartilhar Mapa Unik
              </h2>
              <span style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.5)" }}>
                {mapTitle || "Mapa Estratégico"}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "rgba(255, 255, 255, 0.5)",
              cursor: "pointer",
              padding: "6px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Status de Acesso */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderRadius: "14px",
            backgroundColor: isPublic ? "rgba(16, 185, 129, 0.08)" : "rgba(255, 255, 255, 0.04)",
            border: `1px solid ${isPublic ? "rgba(16, 185, 129, 0.25)" : "rgba(255, 255, 255, 0.08)"}`,
            marginBottom: "20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {isPublic ? (
              <Globe size={18} style={{ color: "#34d399" }} />
            ) : (
              <Lock size={18} style={{ color: "rgba(255, 255, 255, 0.4)" }} />
            )}
            <div>
              <div style={{ fontSize: "13px", fontWeight: 600, color: isPublic ? "#a7f3d0" : "#ffffff" }}>
                {isPublic ? "Link Público Ativado" : "Acesso Privado"}
              </div>
              <div style={{ fontSize: "11px", color: "rgba(255, 255, 255, 0.5)" }}>
                {isPublic
                  ? "Qualquer pessoa com o link pode visualizar e navegar no mapa (somente leitura)."
                  : "Apenas membros autenticados da equipe podem visualizar este mapa."}
              </div>
            </div>
          </div>

          {onTogglePublic && (
            <button
              type="button"
              onClick={handleToggle}
              disabled={toggling}
              style={{
                padding: "6px 14px",
                borderRadius: "10px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: toggling ? "not-allowed" : "pointer",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                backgroundColor: isPublic ? "rgba(239, 68, 68, 0.15)" : "rgba(16, 185, 129, 0.15)",
                color: isPublic ? "#fca5a5" : "#6ee7b7",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {toggling && <Loader2 size={12} className="animate-spin" />}
              {isPublic ? "Desativar" : "Ativar"}
            </button>
          )}
        </div>

        {/* Input com Link e Botão Copiar */}
        <div style={{ marginBottom: "24px" }}>
          <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "rgba(255, 255, 255, 0.7)", marginBottom: "8px" }}>
            Link direto de visualização:
          </label>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              border: "1px solid rgba(255, 255, 255, 0.14)",
              borderRadius: "12px",
              padding: "6px 8px 6px 14px",
            }}
          >
            <input
              type="text"
              readOnly
              value={fullShareUrl}
              onFocus={(e) => e.target.select()}
              style={{
                flex: 1,
                backgroundColor: "transparent",
                border: "none",
                outline: "none",
                fontSize: "12.5px",
                color: "#e2e8f0",
                fontFamily: "monospace",
              }}
            />

            <button
              type="button"
              onClick={handleCopy}
              style={{
                padding: "8px 16px",
                borderRadius: "10px",
                backgroundColor: copied ? "#10b981" : "#c2a06b",
                color: "#ffffff",
                border: "none",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.15s ease",
              }}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              <span>{copied ? "Copiado!" : "Copiar"}</span>
            </button>
          </div>
        </div>

        {/* Ações do Rodapé */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid rgba(255, 255, 255, 0.08)", paddingTop: "16px" }}>
          {fullShareUrl ? (
            <a
              href={fullShareUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: "12px",
                color: "#c2a06b",
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <span>Abrir página pública</span>
              <ExternalLink size={13} />
            </a>
          ) : <div />}

          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 18px",
              borderRadius: "10px",
              border: "1px solid rgba(255, 255, 255, 0.14)",
              backgroundColor: "rgba(255, 255, 255, 0.06)",
              color: "#ffffff",
              fontSize: "12.5px",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
